import { Typography, Box, ButtonGroup, Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Icon, IconButton, Tooltip, Divider, Snackbar, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tab, Tabs } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { RejectionDialog, SendMailDialog } from 'components'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import ApproveDocumentDialog from 'components/ApproveDocumentDialog/ApproveDocumentDialog'
import { AuthContext, DocumentContext } from 'context'
import FileSaver from 'file-saver'
import { DASHBOARD_PATH, DOCUMENT_ACTIONS, DOCUMENT_PATH, DOCUMENT_STATES, getDocumentActions, approveDocumentService, downloadPdfByDocumentFileId, assignPurchaseOrderService, DOCUMENT_TYPE_CODES, rejectDocumentService, DOCUMENT_TYPE_IDS, DOCUMENT_MANAGE_ACTION_ID, updateDocumentStatusService, ADDITIONAL_CREDIT_NOTE_DOCUMENT_ACTIONS, DOCUMENT_APPROVE_MANUALLY_ACTION_ID, DOCUMENT_REJECT_MANUALLY_ACTION_ID, DOCUMENT_SEND_REIMBURSEMENT_ACTION_ID } from 'lib'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { IDocumentAction, IDocumentAdditionalInfo, IDocumentHeader, IMessageConfig, IPurchaseOrderAccoutingInfo, IRejectionCause, ITaxPlan } from 'types'
import Lottie from 'react-lottie'
import askimagen from 'assets/animations/askdocument.json'

interface Props {
    processStatus: number
    documentType?: string
    supplierId?: number
    documentId?: number
    documentData: any
    onReload: () => void
    isManual?: boolean
    header: IDocumentHeader
    documentTypeId: number
    redirectOnApprove?: string
    redirectOnReject?: string
    aditionalInfo: any
    validateReimbusements?: boolean
    aprobacion?: boolean
    actualFlow?: number
}

const DocumentBar = (props: Props) => {

    const { processStatus, documentType, supplierId, documentId, documentData, onReload, isManual, header, documentTypeId, redirectOnApprove, aditionalInfo,
        validateReimbusements, redirectOnReject, aprobacion, actualFlow } = props
    const history = useHistory()
    const { conditions, refreshConditions, documentTypes } = React.useContext(DocumentContext)
    const { user } = React.useContext(AuthContext)
    const [open, setOpen] = React.useState(false)
    const anchorRef = React.useRef<HTMLDivElement>(null)
    const [selected, setSelected] = React.useState<IDocumentAction | undefined>(undefined)
    const [openRejectionDialog, setOpenRejectionDialog] = React.useState(false)
    const [openApproval, setOpenApprovalDialog] = React.useState(false)
    const [openPending, setOpenPending] = React.useState(false)
    const [openSend, setOpenSend] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info" })

    const canApproveByFlow = React.useMemo(() => {

        // FLOW 2 -> validar aprobador específico
        if (actualFlow === 2) {
            const result = user?.userId === documentData?.approverId
            return result
        }

        // FLOW 3 -> Coordinación Contable
        if (actualFlow === 3) {
            const result = user?.rolReembolso === "Coordinación Contable"
            return result
        }

        // FLOW 4 -> Contador General
        if (actualFlow === 4) {
            const result = user?.rolReembolso === "Contador General"
            return result
        }

        // FLOW 5 -> Recursos Humanos
        if (actualFlow === 5) {
            const result = user?.rolReembolso === "Recursos Humanos"
            return result
        }
        return false

    }, [actualFlow, user, documentData])

    const handleClick = () => {
        if (!selected) {
            setMessageConfig({ open: true, message: "Selecciona una acción", severity: "warning" })
            return
        }
        if (selected?.id === DOCUMENT_ACTIONS.reject.id) {
            setOpenRejectionDialog(true)
            return
        }
        if (selected?.id === ADDITIONAL_CREDIT_NOTE_DOCUMENT_ACTIONS.ignore.id) {
            handleIgnore()
            return
        }
        if (selected?.id === DOCUMENT_MANAGE_ACTION_ID
            || selected?.id === DOCUMENT_APPROVE_MANUALLY_ACTION_ID
            || selected?.id === DOCUMENT_REJECT_MANUALLY_ACTION_ID
            || selected?.id === DOCUMENT_SEND_REIMBURSEMENT_ACTION_ID) {
            setOpenSend(true)
        }
    }

    const handleIgnore = async () => {
        try {
            setLoading(true)
            await updateDocumentStatusService(documentId, ADDITIONAL_CREDIT_NOTE_DOCUMENT_ACTIONS.ignore.id, user?.userId)
            setMessageConfig({ open: true, message: "El documento se ha cambiado de estado!", severity: "success" })
            onReload()
            setLoading(false)
            if (redirectOnApprove) {
                setTimeout(() => { history.replace(redirectOnApprove) }, 1500)
            }
        } catch (error) {
            setLoading(false)
            setMessageConfig({ open: true, message: "No se pudo cambiar el estado del documento...", severity: "error" })
        }
    }

    const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, action: IDocumentAction) => {
        if (action.id === DOCUMENT_MANAGE_ACTION_ID
            || action.id === DOCUMENT_APPROVE_MANUALLY_ACTION_ID
            || action.id === DOCUMENT_REJECT_MANUALLY_ACTION_ID
            || action.id === DOCUMENT_SEND_REIMBURSEMENT_ACTION_ID) {
            setSelected(action)
            setOpenSend(true)
        } else {
            setSelected(action)
        }
        setOpen(false)
    }

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen)
    }

    const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return
        }
        setOpen(false)
    }

    const handleDownload = async () => {
        try {
            setLoading(true)
            const result = await downloadPdfByDocumentFileId(documentId)
            FileSaver.saveAs(result, `${documentType}_${documentId}.pdf`)
            setMessageConfig({ open: true, message: "Su descarga ha comenzado", severity: "info" })
            setLoading(false)
        } catch (error) {
            setLoading(false)
        }
    }

    const handleDirectApprove = async () => {
        try {
            setLoading(true)
            await approveDocumentService(
                {
                    documentId,
                    userId: user?.userId,
                    statusId: DOCUMENT_STATES.authorized
                },
                isManual ?
                    { ...documentData, } :
                    { documentData: {} }
            )
            setMessageConfig({ open: true, message: "Documento aprobado!", severity: "success" })
            onReload()
            setLoading(false)
            if (redirectOnApprove) {
                setTimeout(() => { history.replace(redirectOnApprove) }, 1500)
            }
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo aprobar el documento...", severity: "error" })
            setLoading(false)
        }
    }

    const handleApprove = async (taxPlan?: ITaxPlan, purchaseOrder?: IPurchaseOrderAccoutingInfo) => {
        try {
            setLoading(true)
            await approveDocumentService(
                {
                    documentId,
                    userId: user?.userId,
                    statusId: DOCUMENT_STATES.authorized
                },
                isManual ?
                    {
                        ...documentData,
                        taxPlan,
                        accountInfo: { ...documentData.accountInfo, purchaseOrder },
                    } :
                    {
                        documentData: {},
                        taxPlan,
                        accountInfo: { ...documentData.accountInfo, purchaseOrder },
                        reimbursements: documentTypeId === DOCUMENT_TYPE_IDS.reimbursementInvoice ? JSON.parse(aditionalInfo).reimbursements : documentData.reimbursements
                    }
            )
            if (purchaseOrder?.documentId) {
                await assignPurchaseOrderService(purchaseOrder?.documentId, user?.userId, documentId)
            }
            setMessageConfig({ open: true, message: "Documento aprobado!", severity: "success" })
            onReload()
            setLoading(false)
            if (redirectOnApprove) {
                setTimeout(() => { history.replace(redirectOnApprove) }, 1500)
            }
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo aprobar el documento...", severity: "error" })
            setLoading(false)
        }
    }

    const handleReject = async (rejectionCause: IRejectionCause) => {
        try {
            setLoading(true)
            await rejectDocumentService(
                {
                    documentId,
                    userId: user?.userId,
                    statusId: DOCUMENT_STATES.rejected
                },
                isManual ?
                    {
                        ...documentData,
                        rejectionCause
                    } : {
                        ...JSON.parse(aditionalInfo),
                        rejectionCause
                    }
            )
            setMessageConfig({ open: true, message: "Documento rechazado!", severity: "success" })
            onReload()
            setLoading(false)
            if (redirectOnReject) {
                setTimeout(() => { history.replace(redirectOnReject) }, 1500)
            }
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo aprobar el documento...", severity: "error" })
            setLoading(false)
        }
    }

    React.useEffect(() => {
        refreshConditions()
    }, [refreshConditions])

    // Dialogo manual rechazo
    const [openRejectDialog, setOpenRejectDialog] = React.useState(false)
    const [rejectReason, setRejectReason] = React.useState("")
    const [rejectTab, setRejectTab] = React.useState(0)

    const [rejectSuccess, setRejectSuccess] = React.useState(false)
    const [rejectRedirectCounter, setRejectRedirectCounter] = React.useState(10)

    // Dialogo manual confirmacion
    const [openApproveConfirm, setOpenApproveConfirm] = React.useState(false)

    // PARA EL TIMER
    const [approveSuccess, setApproveSuccess] = React.useState(false)
    const [redirectCounter, setRedirectCounter] = React.useState(10)

    React.useEffect(() => {
        let interval: NodeJS.Timeout

        if (approveSuccess && redirectCounter > 0) {
            interval = setInterval(() => {
                setRedirectCounter((prev) => prev - 1)
            }, 1000)
        }

        if (approveSuccess && redirectCounter === 0) {
            history.push('/dashboard')
        }

        return () => {
            if (interval) clearInterval(interval)
        }
    }, [approveSuccess, redirectCounter, history])

    /*    React.useEffect(() => {
   
           let interval: NodeJS.Timeout
   
           if (rejectSuccess && rejectRedirectCounter > 0) {
   
               interval = setInterval(() => {
                   setRejectRedirectCounter((prev) => prev - 1)
               }, 1000)
   
           }
   
           if (rejectSuccess && rejectRedirectCounter === 0) {
               history.push('/dashboard')
           }
   
           return () => {
               if (interval) clearInterval(interval)
           }
   
       }, [rejectSuccess, rejectRedirectCounter, history])
    */

    const rejectOptions = [
        "Información incompleta",
        "Documento inválido",
        "Valores incorrectos",
        "Falta sustento",
        "No cumple políticas",
    ]

    // Acciones aprobar y rechazar documento


    // handleManualApprove actualizado
    const handleManualApprove = async () => {
        try {
            setLoading(true)
            const payload = {
                documentoId: documentId,
                usuarioId: user?.userId,
                accion: "Aprobar",
                motivoRechazo: null
            }
            const response = await fetch(
                `${process.env.REACT_APP_DOCUMENTS_API_SERVICE_DOMAIN}/manual/accion`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }
            )
            if (response.ok) {
                setApproveSuccess(true)
                let seconds = 10
                setRedirectCounter(seconds)
                setMessageConfig({
                    open: true,
                    message: "Documento aprobado correctamente!",
                    severity: "success"
                })
                onReload()
                const interval = setInterval(() => {
                    seconds--
                    setRedirectCounter(seconds)
                    if (seconds <= 0) {
                        clearInterval(interval)
                        history.push('/reembolsos/mydocs')
                    }
                }, 1000)

            } else {
                setMessageConfig({
                    open: true,
                    message: "No se pudo aprobar el documento.",
                    severity: "error"
                })
                setOpenApproveConfirm(false)
            }
            setLoading(false)
        } catch (error) {
            setMessageConfig({
                open: true,
                message: "Error al aprobar el documento.",
                severity: "error"
            })
            setLoading(false)
            setOpenApproveConfirm(false)
        }
    }


    const handleOpenRejectDialog = () => {

        setRejectReason("")
        setRejectTab(0)

        setRejectSuccess(false)
        setRejectRedirectCounter(10)

        setOpenRejectDialog(true)
    }

    // handleManualReject actualizado
    const handleManualReject = async () => {

        if (!rejectReason?.trim()) return
        try {
            setLoading(true)
            const payload = {
                documentoId: documentId,
                usuarioId: user?.userId,
                accion: "Rechazar",
                motivoRechazo: rejectReason
            }
            const response = await fetch(
                `${process.env.REACT_APP_DOCUMENTS_API_SERVICE_DOMAIN}/manual/accion`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }
            )
            if (response.ok) {
                setRejectSuccess(true)
                let seconds = 10
                setRejectRedirectCounter(seconds)
                setMessageConfig({
                    open: true,
                    message: "Documento rechazado correctamente!",
                    severity: "success"
                })
                onReload()
                const interval = setInterval(() => {
                    seconds--
                    setRejectRedirectCounter(seconds)
                    if (seconds <= 0) {
                        clearInterval(interval)
                        history.push('/reembolsos/mydocs')
                    }
                }, 1000)
            } else {
                setMessageConfig({
                    open: true,
                    message: "No se pudo rechazar el documento.",
                    severity: "error"
                })
                setOpenRejectDialog(false)
            }
            setLoading(false)
        } catch (error) {
            setMessageConfig({
                open: true,
                message: "Error al rechazar el documento.",
                severity: "error"
            })
            setLoading(false)
            setOpenRejectDialog(false)
        }
    }

    return (
        <div className="flex py-3 pr-8 items-center pl-4" >
            <div className="flex flex-grow items-center">
                <Tooltip arrow title="Regresar">
                    <IconButton
                        onClick={() => history.action !== "POP" ? history.goBack() : history.push(`${DOCUMENT_PATH}${DASHBOARD_PATH}`)}
                    >
                        <Icon fontSize="small">
                            chevron_left
                        </Icon>
                    </IconButton>
                </Tooltip>
                <div className="ml-3 mr-6" style={{ height: 20 }}>
                    <Divider orientation="vertical" />
                </div>
                <Icon fontSize="small" color="action">
                    {"check"}
                </Icon>
                <div className="flex flex-col pl-4">
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                        {documentType ?? ""}
                    </Typography>
                    <Box color="info.main">
                        <Typography variant="caption" style={{ fontWeight: 600, letterSpacing: 0.5, fontSize: "0.7em" }}>
                            {conditions.find(c => c.conditionId === processStatus)?.name}
                        </Typography>
                    </Box>
                </div>
            </div>
            <div className="flex items-center">
                <Button
                    disabled={loading}
                    size="small"
                    onClick={handleDownload}
                    style={{ marginRight: 15 }}
                    color="primary"
                    disableElevation
                    variant="contained"
                >
                    {"Descargar pdf"}

                </Button>

                {
                    processStatus !== DOCUMENT_STATES.finished && documentTypeId !== DOCUMENT_TYPE_IDS.employeeReimbursement && documentTypeId !== DOCUMENT_TYPE_IDS.cashReimbursement &&
                    <ButtonGroup disableElevation size="small" variant="contained" color={selected ? "primary" : "default"} ref={anchorRef} aria-label="split button">
                        <Button
                            style={{ width: 180 }}
                            onClick={() => !Boolean(user?.canApprove || user?.canReject) ? undefined : handleClick()}
                            disabled={loading}
                            disableTouchRipple={!Boolean(user?.canApprove || user?.canReject)}
                        >
                            {loading ? <CircularProgress color="primary" size={15} thickness={10} /> : selected ? selected.name : conditions.find(c => c.conditionId === processStatus)?.name ?? ""}
                        </Button>
                        {
                            Boolean(user?.canApprove || user?.canReject) &&
                            <Button
                                disabled={loading}
                                size="small"
                                onClick={handleToggle}
                            >
                                <Icon fontSize="small">arrow_drop_down</Icon>
                            </Button>
                        }
                    </ButtonGroup>
                }
                {
                    processStatus !== DOCUMENT_STATES.finished && documentTypeId === DOCUMENT_TYPE_IDS.employeeReimbursement && documentTypeId !== DOCUMENT_TYPE_IDS.cashReimbursement &&
                    <ButtonGroup disableElevation size="small" variant="contained" color={selected ? "primary" : "default"} ref={anchorRef} aria-label="split button">
                        <Button
                            style={{ width: 180 }}
                            onClick={() => handleClick()}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress color="primary" size={15} thickness={10} /> : selected ? selected.name : conditions.find(c => c.conditionId === processStatus)?.name ?? ""}
                        </Button>
                        <Button
                            disabled={loading}
                            size="small"
                            onClick={handleToggle}
                        >
                            <Icon fontSize="small">arrow_drop_down</Icon>
                        </Button>
                    </ButtonGroup>
                }

                {aprobacion

                    && [DOCUMENT_TYPE_IDS.employeeReimbursement, DOCUMENT_TYPE_IDS.cashReimbursement].includes(documentTypeId)
                    && canApproveByFlow
                    &&
                    <ButtonGroup variant="contained" size="small" disableElevation style={{ marginLeft: '40px' }}>
                        {/* Botón izquierda: solo borde y texto rojo */}
                        <Button
                            variant="outlined"
                            style={{ width: 120 }}
                            disabled={loading}
                            onClick={() => setOpenApproveConfirm(true)} // <-- abrir diálogo
                        >
                            {loading ? <CircularProgress color="inherit" size={15} /> : "Aprobar"}
                        </Button>
                        {/* Botón derecha: relleno normal */}
                        <Button
                            style={{ width: 120 }}
                            color="secondary"
                            disabled={loading}
                            onClick={handleOpenRejectDialog}
                        >
                            {loading ? <CircularProgress color="inherit" size={15} /> : "Rechazar"}
                        </Button>
                    </ButtonGroup>
                }

                <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                            }}
                        >
                            <Paper style={{ width: 220 }} >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList dense id="split-button-menu">
                                        {Boolean(user?.canApprove || user?.canReject) && (getDocumentActions(documentTypeId)).map((option) => (
                                            <React.Fragment>
                                                {Boolean(user ? user[option.key] : false) &&
                                                    <MenuItem
                                                        dense
                                                        key={option.name}
                                                        onClick={(event) => handleMenuItemClick(event, option)}
                                                        classes={{ root: "button-list-menu-item" }}
                                                        selected={option.id === selected?.id}
                                                        style={{ padding: 0 }}
                                                    >
                                                        <div className="flex items-center h-full w-full py-1 pr-2 pl-1">
                                                            <Typography className="flex-grow pl-2" variant="caption" style={{ letterSpacing: 0.2 }}>
                                                                {option.name}
                                                            </Typography>
                                                            <Icon color="inherit" fontSize="small">{option.icon}</Icon>
                                                        </div>
                                                    </MenuItem>
                                                }
                                            </React.Fragment>
                                        ))}
                                        {documentTypeId === DOCUMENT_TYPE_IDS.employeeReimbursement &&
                                            !Boolean(user?.canApprove || user?.canReject) && (getDocumentActions(documentTypeId))
                                                .filter((option) => option.id === DOCUMENT_SEND_REIMBURSEMENT_ACTION_ID)
                                                .map((option) => (
                                                    <React.Fragment>
                                                        <MenuItem
                                                            dense
                                                            key={option.name}
                                                            onClick={(event) => handleMenuItemClick(event, option)}
                                                            classes={{ root: "button-list-menu-item" }}
                                                            selected={option.id === selected?.id}
                                                            style={{ padding: 0 }}
                                                        >
                                                            <div className="flex items-center h-full w-full py-1 pr-2 pl-1">
                                                                <Typography className="flex-grow pl-2" variant="caption" style={{ letterSpacing: 0.2 }}>
                                                                    {option.name}
                                                                </Typography>
                                                                <Icon color="inherit" fontSize="small">{option.icon}</Icon>
                                                            </div>
                                                        </MenuItem>
                                                    </React.Fragment>
                                                ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
                <Dialog
                    open={openRejectDialog}
                    onClose={() => {
                        if (!loading && !rejectSuccess) {
                            setOpenRejectDialog(false)
                        }
                    }}
                    maxWidth="sm"
                    fullWidth
                    style={{ padding: '60px' }}
                >

                    {!loading && !rejectSuccess && (
                        <>
                            <DialogTitle>
                                Motivo de rechazo
                            </DialogTitle>

                            <DialogContent>

                                <Tabs
                                    value={rejectTab}
                                    onChange={(e, value) => {
                                        setRejectTab(value)
                                        setRejectReason("")
                                    }}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    style={{ marginBottom: 20 }}
                                >
                                    <Tab label="Opciones" />
                                    <Tab label="Manual" />
                                </Tabs>

                                {rejectTab === 0 && (
                                    <Autocomplete
                                        options={rejectOptions}
                                        value={rejectReason}
                                        onChange={(event, newValue) => {
                                            setRejectReason(newValue || "")
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Selecciona un motivo"
                                                variant="outlined"
                                                fullWidth
                                            />
                                        )}
                                    />
                                )}

                                {rejectTab === 1 && (
                                    <TextField
                                        label="Motivo manual"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                    />
                                )}

                            </DialogContent>

                            <DialogActions>

                                <Button
                                    onClick={() => setOpenRejectDialog(false)}
                                >
                                    Cancelar
                                </Button>

                                <Button
                                    color="secondary"
                                    variant="contained"
                                    disabled={!rejectReason?.trim()}
                                    onClick={handleManualReject}
                                >
                                    Rechazar
                                </Button>

                            </DialogActions>
                        </>
                    )}

                    {loading && (
                        <DialogContent
                            style={{
                                paddingTop: 40,
                                paddingBottom: 40
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 20
                                }}
                            >
                                <CircularProgress />

                                <Typography>
                                    Rechazando documento...
                                </Typography>
                            </div>
                        </DialogContent>
                    )}

                    {!loading && rejectSuccess && (
                        <>
                            <DialogTitle style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: 90,
                                    height: 90,
                                    borderRadius: '50%',
                                    background: '#ffebee',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px auto'
                                }}
                                >
                                    <span style={{ fontSize: 42 }}>
                                        ❌
                                    </span>
                                </div>
                                <Typography variant="h5" style={{ fontWeight: 700 }}>
                                    Documento rechazado
                                </Typography>
                            </DialogTitle>

                            <DialogContent style={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 4 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}                >

                                    <Alert
                                        severity="error"
                                        style={{
                                            width: '100%',
                                            borderRadius: 3
                                        }}
                                    >
                                        El documento fue rechazado correctamente.
                                    </Alert>

                                    <Typography
                                        align="center"
                                        style={{ lineHeight: 1.7 }}
                                    >
                                        Será redirigido automáticamente al inicio en{" "}
                                        <strong style={{ fontSize: '1.1rem' }}>
                                            {rejectRedirectCounter}
                                        </strong>{" "}
                                        segundos.
                                    </Typography>

                                </div>
                            </DialogContent>
                        </>
                    )}

                </Dialog>
                <Dialog open={openApproveConfirm} onClose={() => { if (!loading && !approveSuccess) { setOpenApproveConfirm(false) } }}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ style: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', padding: '40px' } }}>
                    {!loading && !approveSuccess && (
                        <>
                            <DialogTitle style={{ paddingBottom: 1, paddingTop: 3, paddingRight: 4, paddingLeft: 4, textAlign: 'center' }}>
                                <Typography align="center" variant='h6' style={{ fontWeight: 700, marginBottom: '30px' }}>
                                    APROBACIÓN DE DOCUMENTO
                                </Typography>
                                <Lottie
                                    options={{ loop: true, autoplay: true, animationData: askimagen, rendererSettings: { preserveAspectRatio: 'xMidYMid slice' } }}
                                    height={150}
                                    width={150}
                                />
                            </DialogTitle>
                            <DialogContent style={{ paddingRight: 4, paddingLeft: 4, paddingBottom: 2 }}>
                                <Typography align="center" style={{ lineHeight: 1.7, fontSize: '0.97rem', marginTop: '20px' }}>
                                    Verifique que toda la información sea correcta antes de continuar.
                                </Typography>
                            </DialogContent>
                            <DialogActions style={{ paddingRight: 3, paddingLeft: 3, paddingBottom: 3, paddingTop: 1, gap: 1.5, justifyContent: 'center' }}>
                                <Button
                                    onClick={() => setOpenApproveConfirm(false)}
                                    variant="outlined"
                                    color="inherit"
                                    style={{ borderRadius: 3, padding: '10px', textTransform: 'none', fontWeight: 600, marginTop: '20px' }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    onClick={handleManualApprove}
                                    style={{ borderRadius: 3, padding: '10px', textTransform: 'none', fontWeight: 600, marginTop: '20px' }}
                                >
                                    Aprobar documento
                                </Button>
                            </DialogActions>
                        </>
                    )}

                    {loading && (
                        <DialogContent
                            style={{ paddingTop: 7, paddingBottom: 7, paddingRight: 4, paddingLeft: 4 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }} >
                                <div style={{ padding: 20, borderRadius: '50%', background: '#f5f5f5' }}>
                                    <CircularProgress size={42} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Typography variant="h6" style={{ fontWeight: 700 }} gutterBottom>
                                        Procesando aprobación
                                    </Typography>
                                    <Typography color="textSecondary">
                                        Espere un momento mientras se aprueba el documento...
                                    </Typography>
                                </div>
                            </div>
                        </DialogContent>
                    )}

                    {!loading && approveSuccess && (
                        <>
                            <DialogTitle style={{ textAlign: 'center', paddingTop: 4, paddingBottom: 1 }}>
                                <div style={{
                                    width: 90, height: 90, borderRadius: '50%', background: '#e8f5e9', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto'
                                }}>
                                    <span style={{ fontSize: 42 }}>
                                        ✅
                                    </span>
                                </div>
                                <Typography variant="h5" style={{ fontWeight: 700 }}>
                                    Documento aprobado
                                </Typography>
                            </DialogTitle>
                            <DialogContent style={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 4 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                                    <Alert severity="success" style={{ width: '100%', borderRadius: 3 }}>
                                        El documento fue aprobado correctamente.
                                    </Alert>
                                    <Typography align="center" style={{ lineHeight: 1.7 }}>
                                        Será redirigido automáticamente al inicio en{" "}
                                        <strong style={{ fontSize: '1.1rem' }}>
                                            {redirectCounter}
                                        </strong>{" "}
                                        segundos.
                                    </Typography>
                                </div>
                            </DialogContent>
                        </>
                    )}
                </Dialog>
            </div>
            {header && documentData &&
                <ApproveDocumentDialog
                    open={openApproval}
                    onClose={() => setOpenApprovalDialog(false)}
                    onApprove={handleApprove}
                    supplierId={supplierId}
                    requirePo={Boolean((documentData as IDocumentAdditionalInfo)?.header?.supplier?.requirePo) && !Boolean((documentData as IDocumentAdditionalInfo).accountInfo?.purchaseOrder)}
                    requireTaxPlan={!Boolean((documentData as IDocumentAdditionalInfo)?.taxPlan)}
                    identification={header.supplier?.identification}
                    purchaseOrder={documentData.accountInfo?.purchaseOrder}
                    withReimbursementTaxPlan={Boolean(documentTypes.find(d => d.documentCode === DOCUMENT_TYPE_CODES.reimbursementInvoice)?.documentTypeId === documentTypeId)}
                />
            }
            <RejectionDialog
                open={openRejectionDialog}
                onClose={() => setOpenRejectionDialog(false)}
                onReject={handleReject}
            />
            <ConfirmDialog
                open={openPending}
                onCancel={() => setOpenPending(false)}
                onConfirm={async () => true}
                title={"¿Estas seguro de dejar pendiente este documento?"}
            />
            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                    {messageConfig.message}
                </Alert>
            </Snackbar>
            {header &&
                <SendMailDialog
                    open={openSend}
                    onClose={() => setOpenSend(false)}
                    supplier={header.supplier}
                    onSend={() => setMessageConfig({ severity: 'success', message: "Correo enviado!", open: true })}
                    onError={() => setMessageConfig({ severity: 'error', message: "No se pudo enviar el correo...", open: true })}
                    noEmailsMessage={() => setMessageConfig({ severity: 'warning', message: "Agregar al menos un destinatario para continuar", open: true })}
                    invoiceNumber={header.invoiceNumber}
                    documentId={documentId ?? -1}
                    selectedAction={selected}
                    documentData={documentData}
                />
            }
        </div>
    )
}

export default DocumentBar
